import { buildApiUrl } from "../../../config.js";
import { or } from "ajv/dist/compile/codegen";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify"; // Import toast for showing notifications
import { capitalizeWords } from "../../ui/capitalizeWords";
import { InfinitySpin, ProgressBar } from "react-loader-spinner";
import convertToCr from "../../ui/convertToCr";
const OrderItemsTable = ({ orderId, onStatusChange }) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  // Fetch order details function
  const fetchOrderDetails = async (orderId) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/orderItem/orderforstatus/${orderId}`),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setOrderDetails(data.order);
      } else {
        console.error("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update the delivery status of the item
  const updateDeliveryStatus = async (itemId, newStatus) => {
    const updateUrl = `buildApiUrl("/api/orderItem/order-items/${orderId}/update-status/${itemId}`;
    const updatedStatus = { deliveryStatus: newStatus };

    const response = await fetch(updateUrl, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedStatus),
    });

    if (response.ok) {
      const updatedItem = await response.json();
      // Update the state locally after the successful response
      const updatedItems = orderDetails.items.map((item) =>
        item.itemId === itemId ? { ...item, deliveryStatus: newStatus } : item
      );
      setOrderDetails((prevDetails) => ({
        ...prevDetails,
        items: updatedItems,
      }));
      fetchOrderDetails(orderId); // Re-fetch order details in case other changes happened
      onStatusChange(orderId, newStatus);
      // Show success toast
      toast.success(`Delivery status updated to ${newStatus}!`);
    } else {
      // Show error toast if the update fails
      toast.error("Failed to update delivery status!");
    }
  };

  // Fetch order details on mount
  useEffect(() => {
    fetchOrderDetails(orderId); // Fetch order details when the component mounts
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <ProgressBar
          visible={true}
          height="40"
          width="40"
          color="#4fa94d"
          ariaLabel="progress-bar-loading"
          wrapperStyle={{}}
          wrapperClass=""
        />
      </div>
    );
  }

  if (!orderDetails) {
    return <p>No order details found.</p>;
  }

  return (
    <div className="overflow-x-auto mt-1 p-1 bg-white rounded-lg max-w-[95%] mx-auto mb-2">
      <div className="rounded-md overflow-x-auto">
        <table className="min-w-full table-auto rounded-lg overflow-scroll shadow-md border-spacing-0 border-collapse">
          <thead className="bg-gray-700  text-white tracking-wide">
            <tr>
              <th className="px-2 py-1 text-left font-normal">#</th>
              <th className="px-2 py-1 text-left font-normal">Item Name</th>
              <th className="px-2 py-1 text-left font-normal">Category</th>
              <th className="px-2 py-1 text-left font-normal">Item Code</th>
              <th className="px-2 py-1 text-left font-normal">Qty</th>
              <th className="px-2 py-1 text-left font-normal">
                Warehouse Name
              </th>
              <th className="px-2 py-1 text-left font-normal">
                Warehouse Batch
              </th>
              <th className="px-2 py-1 text-left font-normal">Price</th>
              <th className="px-2 py-1 text-left font-normal">Total</th>
              <th className="px-2 py-1 text-left font-normal">
                Item Delivery Status
              </th>
              <th className="px-2 py-1 text-left font-normal">Update Status</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {orderDetails.items?.map((item, index) => (
              <tr
                key={index}
                className=" border border-b divide-x text-gray-700 divide-gray-200 border-collapse border-gray-200 transition-colors duration-200"
              >
                <td className="px-2 py-0.5">{index + 1}</td>
                <td className="px-2 py-0.5">
                  {capitalizeWords(item.itemName)}
                </td>
                <td className="px-2 py-0.5">
                  {capitalizeWords(item.categoryName)}
                </td>
                <td className="px-2 py-0.5">
                  {capitalizeWords(item.itemCode)}
                </td>
                <td className="px-2 py-0.5">{item.qty}</td>
                <td className="px-2 py-0.5">
                  {capitalizeWords(item.wareHouseName)}
                </td>
                <td className="px-2 py-0.5">
                  {capitalizeWords(item.inwardReference)}
                </td>
                <td className="px-2 py-0.5">{item.price}</td>
                <td className="px-2 py-0.5">
                  {convertToCr(item.price * item.qty)}
                </td>
                <td className="px-2 py-0.5">
                  <span
                    className={`px-2 py-0.5 rounded-md ${
                      item.deliveryStatus === "Dispatched"
                        ? "bg-green-100 text-green-700"
                        : "bg-orange-100 text-orange-600"
                    }`}
                  >
                    {item.deliveryStatus}
                  </span>
                </td>
                <td className="px-2 py-0.5 text-[11px] text-gray-700 border border-gray-200 ">
                  <select
                    value={item.deliveryStatus}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      updateDeliveryStatus(item.itemId, newStatus);
                    }}
                    className={`px-2 py-0.5  border border-gray-300 rounded-md text-[11px] cursor-pointer ${
                      item.deliveryStatus === "Delivered"
                        ? "bg-green-100 text-green-700"
                        : item.deliveryStatus === "Dispatched"
                        ? "bg-blue-100 text-blue-700"
                        : item.deliveryStatus === "Partial Dispatched"
                        ? "bg-blue-100 text-blue-700"
                        : item.deliveryStatus === "Pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : item.deliveryStatus === "Cancelled"
                        ? "bg-red-100 text-red-700"
                        : "bg-gray-100 text-gray-700"
                    } ${
                      item.deliveryStatus === "Pending" ||
                      item.deliveryStatus === "Rejected"
                        ? "bg-red-200 text-red-700 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={
                      orderDetails.status === "Pending" ||
                      orderDetails.status === "Rejected"
                    }
                  >
                    <option value="Pending">Pending</option>
                    <option value="Dispatched">Dispatched</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderItemsTable;
